import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShieldCheck, Smartphone } from 'lucide-react'
import { auth } from '../../lib/supabase'

type MfaStep = 'enroll' | 'verify' | 'done'

export function MFASetupPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState<MfaStep>('enroll')
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [secret, setSecret] = useState<string | null>(null)
  const [factorId, setFactorId] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    auth.mfa.enroll({ factorType: 'totp', issuer: 'ASC Survey' }).then(({ data, error: err }) => {
      if (cancelled || err || !data) return
      setFactorId(data.id)
      setQrCode(data.totp.qr_code)
      setSecret(data.totp.secret)
    })
    return () => { cancelled = true }
  }, [])

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault()
    if (!factorId) return
    setIsLoading(true)
    setError(null)
    try {
      const { data: challenge, error: cErr } = await auth.mfa.challenge({ factorId })
      if (cErr || !challenge) throw cErr ?? new Error('Challenge failed')
      const { error: vErr } = await auth.mfa.verify({ factorId, challengeId: challenge.id, code: code.trim() })
      if (vErr) throw vErr
      setStep('done')
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="mx-auto max-w-md px-4 py-8">
      <div className="v11-section overflow-hidden">
        <div className="border-b border-slate-200 bg-white px-6 py-5">
          <div className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-blue-600" />
            <h1 className="font-brand text-lg font-bold text-[var(--brand-deep)]">
              Set up two-factor authentication
            </h1>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            Required for field surveyor accounts to protect facility data.
          </p>
        </div>

        <div className="px-6 py-6">
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {step === 'enroll' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
                <Smartphone size={18} className="shrink-0 text-slate-500" />
                <span>
                  Open your authenticator app (Google Authenticator, Authy, 1Password) and scan the code below.
                </span>
              </div>
              {qrCode ? (
                <div className="flex justify-center">
                  <img src={qrCode} alt="TOTP QR code" className="h-48 w-48 rounded-lg border border-slate-200" />
                </div>
              ) : (
                <div className="flex h-48 items-center justify-center rounded-lg border border-dashed border-slate-300 text-sm text-slate-400">
                  Loading QR code&hellip;
                </div>
              )}
              {secret && (
                <div className="rounded-lg bg-slate-100 px-3 py-2 text-center">
                  <p className="mb-1 text-xs text-slate-500">Manual entry key</p>
                  <code className="font-mono text-sm font-bold tracking-widest text-slate-700">{secret}</code>
                </div>
              )}
              <button
                onClick={() => setStep('verify')}
                disabled={!qrCode}
                className="w-full rounded-lg bg-blue-600 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                I scanned it &mdash; continue
              </button>
            </div>
          )}

          {step === 'verify' && (
            <form onSubmit={handleVerify} className="space-y-4">
              <p className="text-sm text-slate-700">
                Enter the 6-digit code shown in your authenticator app.
              </p>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full rounded-lg border border-slate-300 px-4 py-3 text-center font-mono text-2xl tracking-[0.5em]"
                placeholder="000000"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setStep('enroll')}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isLoading || code.length !== 6}
                  className="flex-1 rounded-lg bg-blue-600 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {isLoading ? 'Verifying\u2026' : 'Verify and enable'}
                </button>
              </div>
            </form>
          )}

          {step === 'done' && (
            <div className="space-y-4 text-center">
              <ShieldCheck size={48} className="mx-auto text-green-500" />
              <p className="font-bold text-slate-800">Two-factor authentication enabled</p>
              <p className="text-sm text-slate-500">Your account is now protected.</p>
              <button
                onClick={() => navigate('/today', { replace: true })}
                className="w-full rounded-lg bg-blue-600 py-2 text-sm font-bold text-white hover:bg-blue-700"
              >
                Continue to surveys
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
